import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { miniLayoutThreshhold } from '../libs/libSupport';

import MasterPage from '../libs/MasterPage';

import app from '../appData';
import { useSessionStore } from '../libs/SamState';
import { MenuItemData } from '../interfaces/lib-api-interfaces';
import { formatImageUrl } from '../libs/ImageFormatter';

const HomeImage = styled.img` 
    width: 100%;
    height: auto;
    margin-bottom: 8px;
`
const StyledFooterContainer = styled.div<{ backColor: string }>`
    background-color: ${props => props.backColor};
    display: flex;
    justify-content: space-between;
    margin: 16px auto 0 auto;
    max-width: 1000px;
    flex-wrap: wrap;
`
const StyledFooterSection = styled.div<{ marginLeft?: number; marginRight?: number; textAlign: string; lineHeight: number }>`
    display: flex;
    flex-direction: column;
    justify-content: start;
    margin-top: 12px;
    margin-left: ${props => props.marginLeft}px;
    margin-right: ${props => props.marginRight}px;
    text-align: ${props => props.textAlign};
    line-height: ${props => props.lineHeight}px;
    font-size: 14px;
    @media (max-width: ${miniLayoutThreshhold}px) {
        font-size: 12px;
    }
`
const StyledSectionHeader = styled.p`
    font-weight: bold;
    margin-bottom: 4px;
    line-height: 12px;
`
const InfoDiv = styled.div`
    margin-top: 8px;
`
const AboutDiv = styled.div`
    margin-left: auto;
    margin-right: auto;
    text-align: left;
    width: 100%;
    max-width: 900px;
    font-size: 16px;
    line-height: 24px;
`
const JacobImage = styled.img`
    float: right;
    width: 30%;
    height: auto;
    margin-left: 8px;
`
const menuLineHeight = 12;
const menuAlign = "left";
const infoLineHeight = 18;
const infoAlign = "center";
const indent = 80;

const Home: React.FC = () => {
    const [rerender, setRerender] = React.useState(false);

    const { getSessionStore } = useSessionStore();

    return (
        <MasterPage menuLoaded={() => setRerender(!rerender)}>
            {getSessionStore("menu") &&
                <React.Fragment>
                    <Link to="/products/silhouettes"><HomeImage src={formatImageUrl("piper_sky.jpg", { graphicsSubfolder: "other" })}
                        alt="Jacob's Silhouette chimes" /></Link>
                    <AboutDiv>
                        <h1>Jacob Sokoloff: Musical Chime Maker</h1>

                        <JacobImage src="/graphics/other/jacob_pic.jpg" alt="Jacob Sokoloff" />
                        <p>Jacob Sokoloff is a thoughtful, articulate man sincerely devoted to his craft &mdash; a man who feels that somehow he has always had a &ldquo;true, organic calling&rdquo; to
                            make musical instruments. As an anthropology student at California State University Long Beach, he studied Japanese Classical and East Indian music.
                            Living in the Santa Cruz Mountains of California, Jacob's first designs were of flutes and beautiful gourd harps made from natural materials using only hand tools.</p>

                        <p>It happened that his mother collected windchimes, so in 1981 Jacob created what he called a &ldquo;musical instrument for the wind&rdquo; as a gift to her.
                            Friends and family were taken with the magical and enchanting sounds produced by these chimes, and encouraged him to make a few more.
                            That he did, selling them at arts and craft shows all over California. He found many people responding to his beautiful musical tones, always delighted to add his latest chimes
                            to their collection.</p>

                        <p>As the business grew in size, Jacob&apos; sister Fern joined full time in 1985 to help with sales. Like two pieces of a puzzle, brother and sister&apos;s talents blended together
                            and the business grew rapidly. Fern enjoyed transforming the business into a wholesale-only company, traveling to the hand crafted juried sections in the wholesale gift shows
                            around the country. In 1991 Fern&apos;s husband Sam Solomon, a software design engineer, came on full time. Sam took over the financial end of the company, ensuring its stability
                            for the long run. He also oversaw production and inventory control, and developed custom computer software to permit rapid turnaround on most orders. The unique talents of each of
                            these three family members gelled to create a successful company.</p>

                        <p>In 1999 Fern opened a retail outlet called Fern&apos;s Garden, which carried the products of artists she met while doing
                            the wholesale trade shows. Fern was proud to feature the work of American artisans as well as ethically made craft from around the world.
                            To this day, Fern&apos;s Garden can be found in Berkeley, California, and Jacob&apos;s chimes can be purchased on its e-commerce site
                            at <a href="https://fernsgarden.com" style={{ textDecoration: "underline" }}>fernsgarden.com</a>.
                        </p>

                        <p>Jacob’s overriding emphasis in the design of his windchimes is the musical quality of the sound. The pitch of each pipe, the relative tuning of the whole chime, and
                            the &ldquo;sweetness&rdquo; or purity of each tone are all factors that he still deals with constantly, decades after creating that first, magical chime. Because this element of sound
                            quality is so fundamental to the success of his chimes, Jacob takes extreme care in making sure the tuning and resonant quality of each pipe meets his exacting standards.
                            He carefully evaluates each new lift load of raw pipe, adjusting for differences in seam and alloy composition which can affect pitch and create dissonant sounds. Out of this
                            evaluation comes a set of &ldquo;masters,&rdquo; which are used to cut the batch of pipe.</p>

                        <p>When asked about the secret of the company’s success, Fern replies: &ldquo;The commitment to quality is our number one concern. After years of consistently high quality products, our
                            customers have come to expect perfect sound and excellent service. Second, we never forget that our primary purpose is to serve our customers. Here at Jacob&apos;s we work on an
                            inventory basis so we are able to quickly ship orders, with no backordering.&rdquo;</p>

                        <p>When describing the rewards of his work, Jacob emphasizes his fundamental desire to make something beautiful: &ldquo;an instrument for the wind.&rdquo;  His greatest pleasure, he says,
                            is &ldquo;in making something that feels perfect every time.&rdquo;</p>
                    </AboutDiv>
                    <StyledFooterContainer backColor={app.themes.backColor25}>
                        <StyledFooterSection textAlign={menuAlign} lineHeight={menuLineHeight} marginLeft={indent}>
                            <StyledSectionHeader>Categories</StyledSectionHeader>
                            <div>
                                {getSessionStore("menu")[0].popupItems!.map((mapEntry: MenuItemData) => {
                                    return (
                                        <Link to={mapEntry.href!} key={mapEntry.caption}><p>{mapEntry.caption}</p></Link>
                                    );
                                })}
                            </div>
                        </StyledFooterSection>
                        <StyledFooterSection textAlign={menuAlign} lineHeight={menuLineHeight}>
                            <StyledSectionHeader>Navigate</StyledSectionHeader>
                            <div>
                                {app.siteMap.map((mapEntry, index) => {
                                    return (
                                        mapEntry.href!.startsWith("http") ? (
                                            <a href={mapEntry.href!} key={index}><p>{mapEntry.caption}</p></a>
                                        ) : (
                                            <Link to={mapEntry.href!} key={index}><p>{mapEntry.caption}</p></Link>
                                        )
                                    );
                                })}
                            </div>
                        </StyledFooterSection>
                        <StyledFooterSection textAlign={infoAlign} lineHeight={infoLineHeight} marginRight={indent}>
                            <StyledSectionHeader>Info</StyledSectionHeader>
                            <InfoDiv>
                                Jacob's Musical Chimes<br />
                                1831 Solano Ave, Ste A<br />
                                Berkeley, CA 94707<br />
                                800-627-5840<br />
                                info@jacobschimes.com<br />
                            </InfoDiv>
                        </StyledFooterSection>
                    </StyledFooterContainer>
                </React.Fragment>
            }
        </MasterPage>
    );
}

export default Home;